import { useEffect, useRef } from 'react'

import { useFormContext } from 'react-hook-form'

import { Space } from '@tribeplatform/gql-client/types'
import { useDomain } from '@tribeplatform/react-components/common'
import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { SpaceImage } from '@tribeplatform/react-components/Space'
import {
  useCollections,
  useSpace,
  useUpdateSpace,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { SectionHeader } from '@tribeplatform/react-ui-kit/SectionHeader'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { SpaceSettingsProps } from './types'

export const FormDirtyUpdater = ({
  setDirty,
  isActive,
}: Pick<SpaceSettingsProps, 'setDirty' | 'isActive'>) => {
  const { formState } = useFormContext()

  useEffect(() => {
    if (isActive) {
      setDirty(formState.isDirty)
    }
  }, [formState.isDirty, isActive, setDirty])

  return null
}

export const SpaceSettingsGeneral = ({
  space,
  onClose,
  isChangingTab,
  confirmTabChange,
  rejectTabChange,
  setDirty,
  setSubmit,
  isActive,
}: SpaceSettingsProps) => {
  const { $t } = useI18n()
  const { domain, subfolder } = useDomain()
  const { data: collections } = useCollections()
  const { data } = useSpace({
    variables: {
      id: space.id,
    },
    fields: {
      authMemberProps: 'all',
      image: 'basic',
      collection: 'basic',
    },
  })
  const { mutateAsync: updateSpace, isLoading } = useUpdateSpace()
  const buttonRef = useRef<HTMLButtonElement>()

  if (data) {
    space = data
  }

  useEffect(() => {
    if (isActive) {
      setSubmit(() => () => buttonRef?.current?.click())
    }
  }, [isActive, setSubmit, buttonRef])

  const defaultValues = (space: Space) => ({
    ...space,
    _private: space?.private,
    collectionId: space?.collection?.id || null,
  })

  return (
    <Form
      defaultValues={defaultValues(space)}
      onSubmit={async (data, methods) => {
        const {
          name,
          description,
          address: { path },
          collectionId,
          _private,
          hidden,
          inviteOnly,
          nonAdminsCanInvite,
        } = data
        try {
          const updatedSpace = await updateSpace({
            id: space?.id,
            input: {
              name,
              description,
              address: { path, exact: true },
              collectionId,
              private: _private,
              hidden,
              inviteOnly,
              nonAdminsCanInvite,
            },
          })
          methods.reset(defaultValues(updatedSpace))
          toast({
            title: $t({
              defaultMessage: '{SPACE_CC} settings updated',
              id: 'Admin.Space.Settings.Updated',
            }),
            status: 'success',
          })
          if (isChangingTab) {
            confirmTabChange()
          } else {
            onClose()
          }
        } catch (e) {
          const error = e?.response?.errors[0]
          if (error?.message) {
            toast({
              title: error.message,
              status: 'error',
            })
          } else {
            toast({
              title: $t({
                defaultMessage: 'Unable to update {SPACE} settings',
                id: 'Admin.Space.Settings.FailedToUpdate',
              }),
              status: 'error',
            })
          }
          if (isChangingTab) {
            rejectTabChange()
          }
        }
      }}
    >
      {({ watch, setValue }) => {
        const [isPrivate] = watch(['_private'])
        if (!isPrivate) {
          setValue('hidden', false)
        }
        return (
          <>
            <FormDirtyUpdater isActive={isActive} setDirty={setDirty} />
            <div className="sm:h-132 flex flex-col">
              <div className="flex flex-col flex-grow overflow-auto p-5">
                <SectionHeader
                  title={$t({
                    defaultMessage: 'Settings',
                    id: 'Generics.Settings',
                  })}
                />
                <div className="space-y-5">
                  <div className="space-y-1">
                    <FormControl.Label>
                      <T defaultMessage="Icon" id="Generics.Icon" />
                    </FormControl.Label>
                    <SpaceImage space={space} size="md" readOnly={false} />
                  </div>
                  <Form.Input
                    label={$t({
                      defaultMessage: 'Name',
                      id: 'Admin.Space.Settings.Name',
                    })}
                    name="name"
                    validation={{
                      required: $t({
                        defaultMessage: '{SPACE_CC} name cannot be empty.',
                        id: 'Admin.Space.Settings.Name.Validation',
                      }),
                    }}
                  />
                  <Form.Textarea
                    label={$t({
                      defaultMessage: 'Description',
                      id: 'Admin.Space.Settings.Description',
                    })}
                    name="description"
                  />
                  <Form.Input
                    label={$t({
                      defaultMessage: 'Web address',
                      id: 'Admin.Space.Settings.WebAddress',
                    })}
                    name="address.path"
                    leadingAddon={`${domain}${subfolder}/`}
                  />
                  <Form.Select
                    label={$t({
                      defaultMessage: '{COLLECTION_CC}',
                      id: 'Generics.Collection',
                    })}
                    name="collectionId"
                    items={[
                      {
                        text: $t({
                          defaultMessage: 'No collection',
                          id: 'Admin.Space.Settings.Collection.None',
                        }),
                        value: null,
                      },
                      ...(collections || [])?.map(collection => ({
                        text: collection?.name,
                        value: collection?.id,
                      })),
                    ]}
                  />
                  <>
                    <Form.Toggle
                      label={$t({
                        defaultMessage: 'Make private',
                        id: 'Admin.Space.Settings.MakePrivate',
                      })}
                      description={$t({
                        defaultMessage:
                          'Only members can see who’s in the space and what they post.',
                        id: 'Admin.Space.Settings.MakePrivate.Description',
                      })}
                      name="_private"
                    />
                    {isPrivate && (
                      <Form.Toggle
                        label={$t({
                          defaultMessage: 'Hide {SPACE}',
                          id: 'Admin.Space.Settings.Hide',
                        })}
                        description={$t({
                          defaultMessage:
                            '{SPACE_CC} will only be visible for its {MEMBERS}.',
                          id: 'Admin.Space.Settings.Hide.Description',
                        })}
                        name="hidden"
                      />
                    )}
                    <Form.Toggle
                      label={$t({
                        defaultMessage: 'Make invite-only',
                        id: 'Admin.Space.Settings.InviteOnly',
                      })}
                      description={$t({
                        defaultMessage:
                          'Allow only people with an invite to join as space members.',
                        id: 'Admin.Space.Settings.InviteOnly.Description',
                      })}
                      name="inviteOnly"
                    />
                    <Form.Toggle
                      label={$t({
                        defaultMessage: 'Anyone can invite',
                        id: 'Admin.Space.Settings.OpenInvite',
                      })}
                      description={$t({
                        defaultMessage:
                          'Allow non-admin members to invite others to the space.',
                        id: 'Admin.Space.Settings.OpenInvite.Description',
                      })}
                      name="nonAdminsCanInvite"
                    />
                  </>
                </div>
              </div>
              <div className="flex justify-end px-5 py-5 border-t space-s-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onClose()}
                >
                  <T defaultMessage="Cancel" id="Generics.Cancel" />
                </Button>
                <Button type="submit" loading={isLoading} ref={buttonRef}>
                  <T defaultMessage="Update" id="Generics.Update" />
                </Button>
              </div>
            </div>
          </>
        )
      }}
    </Form>
  )
}
